import React, { useState, useEffect, useContext } from "react";
import createAuth0Client from "@auth0/auth0-spa-js";
import {
  FRESH_TOKEN_TIMEOUT_MS,
  installSessionRecovery,
  resetSignInRetry,
  withTimeout,
} from "authentication/sessionRecovery";
import { SessionProblemDialog } from "authentication/SessionProblem/SessionProblemDialog";

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

// getUser() answers from the cached ID token, which still says email_verified: false after the
// user clicks the verification link. A fresh token makes Auth0 re-issue it with the current claim.
const getFreshUser = async (auth0Client, cachedUser) => {
  await withTimeout(
    auth0Client.getTokenSilently({ ignoreCache: true }),
    FRESH_TOKEN_TIMEOUT_MS
  );
  return (await auth0Client.getUser()) || cachedUser;
};

export const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  httpClient,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [hasSessionProblem, setHasSessionProblem] = useState(false);

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions);
      setAuth0(auth0FromHook);

      if (
        window.location.search.includes("code=") &&
        window.location.search.includes("state=")
      ) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        onRedirectCallback(appState);
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser();
        setUser(user);
        handleUnverifiedFlow(user);
      }

      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  // Recover Patient Portal requests the API refuses because the saved token is out of date
  useEffect(() => {
    if (!auth0Client || !httpClient) return undefined;
    return installSessionRecovery(httpClient, {
      auth0Client,
      onUnrecoverable: () => setHasSessionProblem(true),
    });
  }, [auth0Client, httpClient]);

  // App redirects to the verify page through the router while needsVerification is set;
  // a full page load here would re-run this check on arrival and reload forever.
  function handleUnverifiedFlow(user) {
    const isUnverified = Boolean(user) && !user.email_verified;
    setNeedsVerification(isUnverified);
    setIsAuthenticated(Boolean(user) && !isUnverified);
  }

  // There are no refresh tokens, so the silent check runs in a hidden Auth0 frame, which fails
  // whenever Auth0 wants an MFA code and then signs the user out locally. Signing in again
  // (Auth0 skips the password while its session lives) brings back a token with the current claim.
  const recheckVerification = async ({ returnTo = "/" } = {}) => {
    try {
      const freshUser = await getFreshUser(auth0Client, user);
      setUser(freshUser);
      handleUnverifiedFlow(freshUser);
      return { isVerified: Boolean(freshUser && freshUser.email_verified), isRedirecting: false };
    } catch (error) {
      console.error("[React-Auth0-Spa][recheckVerification]", error);
      await auth0Client.loginWithRedirect({ appState: { targetUrl: returnTo } });
      return { isVerified: false, isRedirecting: true };
    }
  };

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      console.error("[React-Auth0-Spa][loginWithPopup]",error);
    } finally {
      setPopupOpen(false);
    }
    const user = await auth0Client.getUser();
    setUser(user);
    handleUnverifiedFlow(user);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    const user = await auth0Client.getUser();
    setLoading(false);
    setUser(user);
    handleUnverifiedFlow(user);
  };

  const logoutUser = async (...p) => {
    auth0Client.logout(...p);
    localStorage.clear();
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        needsVerification,
        recheckVerification,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: (...p) => auth0Client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client.loginWithRedirect(...p),
        getTokenSilently: (...p) => auth0Client.getTokenSilently(...p),
        getTokenWithPopup: (...p) => auth0Client.getTokenWithPopup(...p),
        logout: logoutUser,
      }}
    >
      {children}
      <SessionProblemDialog
        open={hasSessionProblem}
        onSignOut={() => {
          // The family chose to start over: give them one more automatic sign-in
          resetSignInRetry();
          logoutUser({ returnTo: window.location.origin });
        }}
      />
    </Auth0Context.Provider>
  );
};
